<template>
  <div class="container card-container">
    <div v-if="user" class="card shadow-sm p-3 position-relative">
      <div class="card shadow-sm p-3 position-relative">
        <button
            type="button"
            class="profile-back-button"
            @click="$router.back()"
        >
          <i class="fas fa-arrow-left"></i>
          <span>{{ $t('profile.btn_back') }}</span>
        </button>

        <!-- Imagen perfil -->
        <img :src="user.img_path" class="profile-img" alt="Foto de perfil" />

        <!-- Botón editar -->
        <button
            v-if="canEdit"
            class="btn btn-sm btn-outline-secondary edit-btn"
            @click="$emit('edit-profile')"
        >
          <i class="fas fa-pen"></i>
        </button>


        <div class="card-body text-center mt-5">
          <h3 class="card-title mb-1">{{ user.name }} {{ user.surname }}</h3>
          <p class="text-muted small mb-2">
            {{ $t('profile.since') }} {{ user.slackline_since }}
            <template v-if="user.country && user.country.iso2">
              ·
              <flag
                  :iso="user.country.iso2.toLowerCase()"
                  class="me-1"
                  style="width: 20px; height: 15px"
              />
              {{ user.country.name }}
            </template>
          </p>

          <h5>"{{ user.username }}"</h5>

          <!-- Verificación -->
          <div class="mb-2">
            <span :class="['badge', 'bg-' + verificationStatus.type, verificationStatus.type === 'warning' ? 'text-dark' : '']">
              {{ verificationStatus.label }}
            </span>
          </div>

          <!-- Iconos de redes sociales -->
          <div v-if="hasAnySocialLink" class="social-icons mt-3 mb-3">
            <a
                v-if="parsedSocialLinks.website"
                :href="formatWeb(parsedSocialLinks.website)"
                target="_blank"
                rel="noopener"
                class="me-3"
            >
              <i class="fas fa-globe fa-lg"></i>
            </a>
            <a
                v-if="parsedSocialLinks.instagram"
                :href="formatInstagram(parsedSocialLinks.instagram)"
                target="_blank"
                rel="noopener"
                class="me-3"
            >
              <i class="fab fa-instagram fa-lg"></i>
            </a>
            <a
                v-if="parsedSocialLinks.youtube"
                :href="formatYoutube(parsedSocialLinks.youtube)"
                target="_blank"
                rel="noopener"
                class="me-3"
            >
              <i class="fab fa-youtube fa-lg"></i>
            </a>
          </div>

          <!-- Línea de sponsor / grupo / ISA -->
          <div class="d-flex flex-wrap justify-content-center gap-3 mb-3 info-line">
            <div class="d-flex align-items-center gap-2 pill">
              <i class="fa-solid fa-handshake"></i>
              <span>{{ sponsorLabel }}</span>
            </div>
            <div class="d-flex align-items-center gap-2 pill">
              <i class="fa-solid fa-users"></i>
              <span>{{ groupLabel }}</span>
            </div>
            <div class="d-flex align-items-center gap-2 pill" :class="{ 'text-success': isaMember, 'text-muted': !isaMember }">
              <i class="fa-solid fa-shield-heart"></i>
              <span>{{ isaMemberText }}</span>
            </div>
          </div>

          <!-- Roles -->
          <div class="section-box text-center">
            <div class="d-flex justify-content-center align-items-center mb-2">
              <div class="section-title mb-0">{{ $t('profile.role_title') }}</div>
            </div>
            <div class="roles-list" v-if="user && Array.isArray(user.roles) && user.roles.length">
            <span
                v-for="role in user.roles"
                :key="role.id"
                class="bg-secondary badge me-1"
            >
              {{ role.name }}
            </span>
            </div>
          </div>

          <div class="stats-section">
            <div v-if="showStatsTabs" class="stats-tabs">
              <button
                  v-for="tab in availableStatsTabs"
                  :key="tab"
                  type="button"
                  class="stats-tab"
                  :class="{ active: activeStatsRole === tab }"
                  @click="$emit('change-stats-tab', tab)"
              >
                {{ tabLabel(tab) }}
              </button>
            </div>

            <div class="stats-header">
              <div>
                <p class="section-label">{{ statsSectionTitle }}</p>
                <h4 class="stats-heading">{{ statsSectionSubtitle }}</h4>
              </div>
              <div class="count-pill">
                {{ statsCountPill }}
              </div>
            </div>

            <div v-if="statsLoading" class="stats-loading-state">
              {{ tOr('profile.stats.loading', 'Loading statistics...') }}
            </div>

            <template v-else-if="activeStatsRole === 'athlete'">

            <div class="stats-summary">
              <div class="summary-card">
                <span class="summary-label">{{ $t('profile.stats.competitions_count') }}</span>
                <span class="summary-value">{{ profileStats.athlete.competitions_count }}</span>
              </div>
              <div class="summary-card">
                <span class="summary-label">{{ $t('profile.stats.podiums') }}</span>
                <div class="podium-badges">
                  <div class="podium-badge gold">
                    <span>{{ $t('profile.stats.first') }}</span>
                    <strong>{{ profileStats.athlete.top_positions.first }}</strong>
                  </div>
                  <div class="podium-badge silver">
                    <span>{{ $t('profile.stats.second') }}</span>
                    <strong>{{ profileStats.athlete.top_positions.second }}</strong>
                  </div>
                  <div class="podium-badge bronze">
                    <span>{{ $t('profile.stats.third') }}</span>
                    <strong>{{ profileStats.athlete.top_positions.third }}</strong>
                  </div>
                </div>
              </div>
            </div>

            <div v-if="athleteHighlights.length" class="stats-highlights">
              <div
                  v-for="highlight in athleteHighlights"
                  :key="highlight.key"
                  class="highlight-card"
              >
                <span class="summary-label">{{ highlight.label }}</span>
                <strong class="highlight-value">{{ highlight.value }}</strong>
              </div>
            </div>

            <div v-if="athleteStageHighlights.length" class="stage-rounds-section">
              <div class="column-header stage-rounds-header">
                <h5>{{ tOr('profile.stats.best_rounds_by_stage', 'Best rounds by stage') }}</h5>
              </div>
              <div class="stats-highlights stats-highlights-compact">
                <div
                    v-for="stage in athleteStageHighlights"
                    :key="stage.key"
                    class="highlight-card stage-highlight-card"
                >
                  <span class="summary-label">{{ stage.label }}</span>
                  <small class="stage-highlight-caption">{{ tOr('profile.stats.best_round_score_raw_short', 'Best raw score') }}</small>
                  <strong class="highlight-value">{{ stage.bestScoreDisplay }}</strong>
                  <small class="stage-highlight-meta">{{ stage.roundsCountLabel }}</small>
                </div>
              </div>
            </div>

            <div v-if="showAthleteAnalytics" class="stats-analytics">
              <div class="analytics-card">
                <div class="column-header">
                  <h5>{{ tOr('profile.stats.trick_categories', 'Trick categories') }}</h5>
                </div>
                <div class="chart-box">
                  <canvas ref="trickCategoryChart"></canvas>
                </div>
              </div>

              <div class="analytics-card">
                <div class="column-header">
                  <h5>{{ tOr('profile.stats.tricks_by_level', 'Tricks by level') }}</h5>
                </div>
                <div class="chart-box">
                  <canvas ref="trickLevelChart"></canvas>
                </div>
              </div>
            </div>

            <div class="stats-columns">
              <div class="stats-column">
                <div class="column-header">
                  <h5>{{ $t('profile.stats.competitions_list') }}</h5>
                  <span class="column-count">{{ profileStats.athlete.competitions.length }}</span>
                </div>
                <div v-if="profileStats.athlete.competitions.length" class="competition-list">
                  <div
                      v-for="competition in profileStats.athlete.competitions"
                      :key="competition.competition_id"
                      class="competition-item"
                      role="button"
                      tabindex="0"
                      @click.prevent="navigateToCompetition(competition.competition_id)"
                      @keypress.enter.prevent="navigateToCompetition(competition.competition_id)"
                  >
                    <img
                        v-if="competition.competition_img"
                        :src="competition.competition_img"
                        alt="Competition"
                        class="competition-avatar"
                    />
                    <div class="competition-info">
                      <p class="competition-name">{{ competition.competition_name }}</p>
                      <div class="competition-meta">
                        <span
                            v-if="getCompetitionCountryCode(competition)"
                            class="competition-country"
                        >
                          <span class="country-flag" aria-hidden="true">
                            {{ codeToFlag(getCompetitionCountryCode(competition)) }}
                          </span>
                          <span class="country-code">
                            {{ getCompetitionCountryName(competition) || getCompetitionCountryCode(competition) }}
                          </span>
                        </span>
                        <span v-else class="meta-fallback">{{ $t('profile.stats.country_unknown') }}</span>
                        <span class="meta-divider">•</span>
                        <span class="competition-date">{{ formatEventDate(competition) }}</span>
                      </div>
                    </div>
                  </div>
                </div>
                <p v-else class="stats-empty">{{ $t('profile.stats.competitions_empty') }}</p>
              </div>

              <div class="stats-column">
                <div class="column-header">
                  <h5>{{ $t('profile.stats.placements') }}</h5>
                  <span class="column-count">{{ profileStats.athlete.placements.length }}</span>
                </div>
                <div v-if="profileStats.athlete.placements.length" class="placement-list">
                  <div
                      v-for="placement in profileStats.athlete.placements"
                      :key="placement.competition_id + '-' + placement.position"
                      class="placement-item"
                      role="button"
                      tabindex="0"
                      @click.prevent="navigateToScore(placement)"
                      @keypress.enter.prevent="navigateToScore(placement)"
                  >
                    <div class="placement-medal" :class="medalClass(placement.position)">
                      {{ placement.position }}º
                    </div>
                    <div class="placement-info">
                      <p class="competition-name mb-1">{{ placement.competition_name }}</p>
                      <div class="placement-score-wrapper" v-if="placement.score !== null && placement.score !== undefined">
                        <span class="score-badge centered">
                          {{ $t('profile.stats.score_label') }}: {{ placement.score }}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
                <p v-else class="stats-empty">{{ $t('profile.stats.placements_empty') }}</p>
              </div>
            </div>

            <div class="stats-columns stats-columns-secondary">
              <div class="stats-column">
                <div class="column-header">
                  <h5>{{ tOr('profile.stats.top_tricks', 'Top tricks') }}</h5>
                  <span class="column-count">{{ topTricks.length }}</span>
                </div>
                <div v-if="topTricks.length" class="detail-list">
                  <div
                      v-for="trick in topTricks"
                      :key="trick.trick_id"
                      class="detail-item"
                  >
                    <div class="detail-main">
                      <p class="competition-name mb-1">{{ trick.name }}</p>
                      <div class="detail-meta">
                        <span class="meta-pill">{{ trick.level || tOr('profile.stats.level_unknown', 'Unknown level') }}</span>
                      </div>
                    </div>
                    <div class="detail-side">
                      <span class="score-badge centered">{{ formatStatValue(trick.best_score) }}</span>
                      <small class="detail-side-note">{{ trick.validated_count }}x</small>
                    </div>
                  </div>
                </div>
                <p v-else class="stats-empty">{{ tOr('profile.stats.top_tricks_empty', 'No validated tricks yet') }}</p>
              </div>

              <div class="stats-column">
                <div class="column-header">
                  <h5>{{ tOr('profile.stats.tricks_by_level', 'Tricks by level') }}</h5>
                  <span class="column-count">{{ trickLevels.length }}</span>
                </div>
                <div v-if="trickLevels.length" class="detail-list">
                  <div
                      v-for="level in trickLevels"
                      :key="level.level_id || level.label"
                      class="detail-expandable"
                  >
                    <button
                        type="button"
                        class="detail-item detail-toggle"
                        :class="{ expanded: isLevelExpanded(level) }"
                        @click="toggleLevel(level)"
                    >
                      <div class="detail-main">
                        <p class="competition-name mb-1">{{ level.label }}</p>
                        <div class="detail-meta">
                          <span>{{ tOr('profile.stats.validated_tricks', 'Validated tricks') }}: {{ level.validated_count }}</span>
                          <span class="meta-divider">•</span>
                          <span>{{ tOr('profile.stats.unique_tricks', 'Unique tricks') }}: {{ level.unique_count }}</span>
                        </div>
                      </div>
                      <div class="detail-side detail-side-inline">
                        <span class="level-pill">{{ level.unique_count }}</span>
                        <span class="detail-chevron">{{ isLevelExpanded(level) ? '−' : '+' }}</span>
                      </div>
                    </button>

                    <div v-if="isLevelExpanded(level)" class="level-tricks-panel">
                      <div v-if="level.unique_tricks && level.unique_tricks.length" class="level-tricks-list">
                        <div
                            v-for="trick in level.unique_tricks"
                            :key="levelKey(level) + '-' + trick.trick_id"
                            class="level-trick-item"
                        >
                          <div class="detail-main">
                            <p class="competition-name mb-0">{{ trick.name }}</p>
                          </div>
                          <div class="detail-side detail-side-inline">
                            <small class="detail-side-note">{{ trick.validated_count }}x</small>
                          </div>
                        </div>
                      </div>
                      <p v-else class="stats-empty mb-0">{{ tOr('profile.stats.level_tricks_empty', 'No unique tricks in this level yet') }}</p>
                    </div>
                  </div>
                </div>
                <p v-else class="stats-empty">{{ tOr('profile.stats.tricks_by_level_empty', 'No level data yet') }}</p>
              </div>
            </div>

            <div class="stats-columns stats-columns-secondary">
              <div class="stats-column">
                <div class="column-header">
                  <h5>{{ tOr('profile.stats.combos', 'Combos') }}</h5>
                  <span class="column-count">{{ comboStats.total }}</span>
                </div>
                <div v-if="comboSpotlights.length" class="combo-panels">
                  <div
                      v-for="spotlight in comboSpotlights"
                      :key="spotlight.key"
                      class="detail-expandable"
                  >
                    <button
                        type="button"
                        class="detail-item detail-toggle"
                        :class="{ expanded: isComboExpanded(spotlight) }"
                        @click="toggleCombo(spotlight)"
                    >
                      <div class="detail-main">
                        <p class="competition-name mb-1">{{ spotlight.badges.join(' / ') }}</p>
                        <div class="detail-meta combo-card-meta">
                          <span>{{ spotlight.combo.tricks_count }} {{ tOr('profile.stats.tricks', 'tricks') }}</span>
                          <span v-if="spotlight.combo.competition_name" class="meta-pill">{{ spotlight.combo.competition_name }}</span>
                          <span v-if="spotlight.combo.round_name" class="meta-pill">{{ spotlight.combo.round_name }}</span>
                        </div>
                      </div>
                      <div class="detail-side detail-side-inline">
                        <span class="score-badge centered">{{ formatStatValue(spotlight.combo.total_score) }}</span>
                        <span class="detail-chevron">{{ isComboExpanded(spotlight) ? '−' : '+' }}</span>
                      </div>
                    </button>

                    <div v-if="isComboExpanded(spotlight)" class="level-tricks-panel">
                      <div class="combo-components-list">
                        <div
                            v-for="(trick, index) in spotlight.combo.components"
                            :key="spotlight.key + '-' + index"
                            class="combo-component-item"
                        >
                          <span class="combo-index">{{ index + 1 }}</span>
                        <div class="detail-main">
                          <p class="competition-name mb-1">{{ trick.name }}</p>
                          <div class="detail-meta">
                            <span>{{ trick.subname || tOr('profile.stats.no_subname', 'No subname') }}</span>
                            <span v-if="trick.variant_label" class="meta-pill">{{ trick.variant_label }}</span>
                            <span v-if="trick.grab_label" class="meta-pill">{{ trick.grab_label }}</span>
                            <span v-if="trick.dub_label" class="meta-pill meta-pill-warning">{{ trick.dub_label }}</span>
                          </div>
                        </div>
                        <div class="detail-side">
                          <span class="score-badge centered">{{ formatStatValue(trick.score) }}</span>
                        </div>
                      </div>
                    </div>
                    </div>
                  </div>
                </div>
                <p v-else class="stats-empty">{{ tOr('profile.stats.combos_empty', 'No combos recorded yet') }}</p>
              </div>

              <div class="stats-column">
                <div class="column-header penalty-column-header">
                  <div class="penalty-header-main">
                    <h5>{{ tOr('profile.stats.penalties', 'Penalties') }}</h5>
                    <div v-if="penaltySummaryItems.length" class="penalty-summary-pills penalty-summary-pills-inline">
                      <span
                          v-for="item in penaltySummaryItems"
                          :key="item.label"
                          class="penalty-summary-pill"
                      >
                        {{ item.label }}: {{ item.count }}
                      </span>
                    </div>
                  </div>
                  <span class="column-count">{{ penaltyStats.total }}</span>
                </div>
                <div v-if="penaltyTypes.length" class="detail-list penalty-list">
                  <div
                      v-for="penalty in penaltyTypes"
                      :key="penalty.dub_id || penalty.label"
                      class="detail-expandable"
                  >
                    <button
                        type="button"
                        class="detail-item detail-toggle"
                        :class="{ expanded: isPenaltyExpanded(penalty) }"
                        @click="togglePenalty(penalty)"
                    >
                      <div class="detail-main">
                        <p class="competition-name mb-1">{{ penalty.label }}</p>
                        <div class="detail-meta">
                          <span>{{ penalty.count }} {{ tOr('profile.stats.affected_tricks', 'affected tricks') }}</span>
                        </div>
                      </div>
                      <div class="detail-side detail-side-inline">
                        <span class="level-pill">{{ penalty.count }}</span>
                        <span class="detail-chevron">{{ isPenaltyExpanded(penalty) ? '−' : '+' }}</span>
                      </div>
                    </button>

                    <div v-if="isPenaltyExpanded(penalty)" class="level-tricks-panel">
                      <div v-if="penalty.affected_tricks && penalty.affected_tricks.length" class="level-tricks-list">
                        <div
                            v-for="trick in penalty.affected_tricks"
                            :key="penaltyKey(penalty) + '-' + trick.trick_id + '-' + (trick.variation_id || 'n') + '-' + (trick.grab_id || 'n')"
                            class="level-trick-item"
                        >
                          <div class="detail-main">
                            <p class="competition-name mb-1">{{ trick.name }}</p>
                            <div class="detail-meta">
                              <span>{{ trick.subname || tOr('profile.stats.no_subname', 'No subname') }}</span>
                              <span v-if="trick.variant_label" class="meta-pill">{{ trick.variant_label }}</span>
                              <span v-if="trick.grab_label" class="meta-pill">{{ trick.grab_label }}</span>
                              <span v-if="trick.dub_label" class="meta-pill meta-pill-warning">{{ trick.dub_label }}</span>
                            </div>
                          </div>
                          <div class="detail-side">
                            <span class="score-badge centered">{{ formatStatValue(trick.best_score) }}</span>
                            <small class="detail-side-note">{{ trick.validated_count }}x</small>
                          </div>
                        </div>
                      </div>
                      <p v-else class="stats-empty mb-0">{{ tOr('profile.stats.penalties_empty', 'No penalties recorded yet') }}</p>
                    </div>
                  </div>
                </div>
                <p v-else class="stats-empty">{{ tOr('profile.stats.penalties_empty', 'No penalties recorded yet') }}</p>
              </div>
            </div>

            </template>

            <template v-else-if="activeStatsRole === 'judge'">
              <div class="stats-summary judge-summary judge-tab-summary">
                <div class="summary-card judge-card">
                  <span class="summary-label">{{ $t('profile.stats.judge_events_label') }}</span>
                  <span class="summary-value">{{ judgeStats.competitions_count }}</span>
                </div>
                <div class="summary-card judge-card">
                  <span class="summary-label">{{ $t('profile.stats.judge_rounds_label') }}</span>
                  <span class="summary-value">{{ judgeStats.rounds_scored_count }}</span>
                </div>
              </div>

              <div v-if="judgeHighlights.length" class="stats-highlights">
                <div
                    v-for="highlight in judgeHighlights"
                    :key="highlight.key"
                    class="highlight-card"
                >
                  <span class="summary-label">{{ highlight.label }}</span>
                  <strong class="highlight-value">{{ highlight.value }}</strong>
                </div>
              </div>

              <div v-if="judgeStageHighlights.length" class="stage-rounds-section">
                <div class="column-header stage-rounds-header">
                  <h5>{{ tOr('profile.stats.rounds_by_stage', 'Rounds by stage') }}</h5>
                </div>
                <div class="stats-highlights stats-highlights-compact">
                  <div
                      v-for="stage in judgeStageHighlights"
                      :key="stage.key"
                      class="highlight-card stage-highlight-card"
                  >
                    <span class="summary-label">{{ stage.label }}</span>
                    <strong class="highlight-value">{{ stage.value }}</strong>
                  </div>
                </div>
              </div>

              <div class="stats-columns stats-columns-secondary judge-detail-columns">
                <div class="stats-column judge-detail-column">
                  <div class="column-header">
                    <h5>{{ $t('profile.stats.judge_best_time_label') }}</h5>
                  </div>
                  <div v-if="judgeFastestRound" class="judge-detail-card">
                    <div class="detail-main">
                      <p class="competition-name mb-1">{{ judgeFastestRound.competition_name }}</p>
                      <div class="detail-meta">
                        <span v-if="judgeFastestRound.athlete_name" class="meta-pill">{{ judgeFastestRound.athlete_name }}</span>
                        <span v-if="judgeFastestRound.stage_label" class="meta-pill">{{ judgeFastestRound.stage_label }}</span>
                        <span v-if="judgeFastestRound.round_name">{{ judgeFastestRound.round_name }}</span>
                      </div>
                    </div>
                    <div class="detail-side">
                      <span class="score-badge centered">{{ formatDurationSeconds(judgeFastestRound.duration_seconds) }}</span>
                    </div>
                  </div>
                  <p v-else class="stats-empty">{{ $t('profile.stats.judge_best_time_empty') }}</p>
                </div>

                <div class="stats-column judge-detail-column">
                  <div class="column-header">
                    <h5>{{ $t('profile.stats.judge_best_round_label') }}</h5>
                  </div>
                  <div v-if="judgeBestRound" class="judge-detail-card">
                    <div class="detail-main">
                      <p class="competition-name mb-1">{{ judgeBestRound.competition_name }}</p>
                      <div class="detail-meta">
                        <span v-if="judgeBestRound.athlete_name" class="meta-pill">{{ judgeBestRound.athlete_name }}</span>
                        <span v-if="judgeBestRound.stage_label" class="meta-pill">{{ judgeBestRound.stage_label }}</span>
                        <span v-if="judgeBestRound.round_name">{{ judgeBestRound.round_name }}</span>
                      </div>
                    </div>
                    <div class="detail-side">
                      <span class="score-badge centered">{{ formatStatValue(judgeBestRound.total_score) }}</span>
                    </div>
                  </div>
                  <p v-else class="stats-empty">{{ $t('profile.stats.judge_best_round_empty') }}</p>
                </div>
              </div>
            </template>

            <template v-else-if="activeStatsRole === 'organizer'">
              <div class="stats-summary organizer-summary organizer-tab-summary">
                <div class="summary-card organizer-card">
                  <span class="summary-label">{{ $t('profile.stats.organizer_events_label') }}</span>
                  <span class="summary-value">{{ organizerStats.competitions_count }}</span>
                </div>
              </div>
            </template>
          </div>
        </div>

      </div>
    </div>
    <div v-else class="alert alert-warning text-center">
      {{ $t('profile.profile_not_available') }}
    </div>
  </div>
</template>

<script>
import Chart from 'chart.js/auto';
import echoService from "@/services/echoService";
import {mapActions} from "vuex";

export default {
  name: 'ProfileView',
  props: {
    user: Object,
    canEdit: {
      type: Boolean,
      default: false
    },
    availableStatsTabs: {
      type: Array,
      default: () => []
    },
    activeStatsTab: {
      type: String,
      default: null
    },
    statsLoading: {
      type: Boolean,
      default: false
    }
  },
  data(){
    return {
      verificationStatusMessage: null, // mensaje opcional temporal
      verificationStatusType: null,     // puede ser 'info', 'success', 'error'
      expandedLevels: [],
      expandedCombos: [],
      expandedPenalties: [],
      chartRenderNonce: 0,
      trickCategoryChartInstance: null,
      trickLevelChartInstance: null,
    }
  },
  computed: {
    verificationStatus() {
      if (this.verificationStatusMessage) {
        return {
          label: this.verificationStatusMessage,
          type: this.verificationStatusType || 'info'
        };
      }

      if (this.user.email_verified_at) {
        return {
          label: this.$t('profile.mail_verified'),
          type: 'success'
        };
      }

      if (this.user.email_verification_sent_at) {
        return {
          label: this.$t('profile.mail_pending'),
          type: 'warning'
        };
      }

      return {
        label: this.$t('profile.mail_not_verified'),
        type: 'danger'
      };
    },
    hasAnySocialLink() {
      return (
          this.parsedSocialLinks.website ||
          this.parsedSocialLinks.instagram ||
          this.parsedSocialLinks.youtube
      );
    },
    parsedSocialLinks() {
      try {
        return typeof this.user.social_links === 'string'
            ? JSON.parse(this.user.social_links)
            : this.user.social_links || {};
      } catch (e) {
        console.error('Error parsing social_links:', e);
        return {};
      }
    },
    profileStats() {
      const stats = this.user?.stats || {};
      const sanitizeArray = value => Array.isArray(value) ? value : [];
      const normalizeNumber = value => {
        const parsed = Number(value);
        return Number.isFinite(parsed) ? parsed : 0;
      };
      const normalizeNullableNumber = value => {
        const parsed = Number(value);
        return Number.isFinite(parsed) ? parsed : null;
      };
      const athleteSource = stats.athlete && Object.keys(stats.athlete).length ? stats.athlete : stats;
      const athleteSummary = athleteSource.summary || {};
      const podium = athleteSummary.top_positions || athleteSource.top_positions || {};
      const athlete = {
        competitions_count: normalizeNumber(athleteSummary.competitions_count ?? athleteSource.competitions_count),
        competitions: sanitizeArray(athleteSource.participations || athleteSource.competitions),
        placements: sanitizeArray(athleteSource.placements),
        tricks: athleteSource.tricks || { totals: {}, top_tricks: [], by_level: [], by_category: [] },
        combos: athleteSource.combos || { total: 0, longest_combo: null, highest_scoring_combo: null, top_combos: [] },
        penalties: athleteSource.penalties || { available: false, total: 0, summary: {}, by_type: [] },
        rounds: athleteSource.rounds || { totals: {}, averages: {}, recent: [], by_stage: [] },
        stages: athleteSource.stages || { averages: [], counts: [] },
        charts: athleteSource.charts || {},
        summary: {
          validated_tricks_count: normalizeNumber(athleteSummary.validated_tricks_count),
          unique_tricks_count: normalizeNumber(athleteSummary.unique_tricks_count),
          rounds_count: normalizeNumber(athleteSummary.rounds_count),
          scored_rounds_count: normalizeNumber(athleteSummary.scored_rounds_count),
          average_round_score: normalizeNullableNumber(athleteSummary.average_round_score),
          average_round_score_normalized: normalizeNullableNumber(
            athleteSummary.average_round_score_normalized ?? athleteSummary.average_round_score
          ),
          average_round_score_raw: normalizeNullableNumber(athleteSummary.average_round_score_raw),
          best_round_score: normalizeNullableNumber(athleteSummary.best_round_score),
          best_round_score_normalized: normalizeNullableNumber(athleteSummary.best_round_score_normalized),
          best_final_position: normalizeNullableNumber(athleteSummary.best_final_position),
          average_final_position: normalizeNullableNumber(athleteSummary.average_final_position),
          tricklist_coverage_percentage: normalizeNullableNumber(athleteSummary.tricklist_coverage_percentage),
          round_duration_unit: athleteSummary.round_duration_unit || null,
        },
        top_positions: {
          first: normalizeNumber(podium.first),
          second: normalizeNumber(podium.second),
          third: normalizeNumber(podium.third),
        }
      };
      const judgeSource = stats.judge || {};
      const judge = {
        competitions_count: normalizeNumber(judgeSource.competitions_count),
        rounds_scored_count: normalizeNumber(judgeSource.rounds_scored_count),
        online_competitions_count: normalizeNumber(judgeSource.online_competitions_count),
        physical_competitions_count: normalizeNumber(judgeSource.physical_competitions_count),
        technical_operator_competitions_count: normalizeNumber(judgeSource.technical_operator_competitions_count),
        stage_counts: sanitizeArray(judgeSource.stage_counts),
        best_time_seconds: normalizeNullableNumber(judgeSource.best_time_seconds),
        fastest_round: judgeSource.fastest_round || null,
        best_round: judgeSource.best_round || null,
      };
      const organizerSource = stats.organizer || {};
      const organizer = {
        competitions_count: normalizeNumber(organizerSource.competitions_count)
      };
      return {
        athlete,
        judge,
        organizer
      };
    },
    judgeStats() {
      return this.profileStats.judge;
    },
    judgeHighlights() {
      return [
        {
          key: 'judge-online-events',
          label: this.$t('profile.stats.judge_online_events_label'),
          value: this.judgeStats.online_competitions_count,
        },
        {
          key: 'judge-physical-events',
          label: this.$t('profile.stats.judge_physical_events_label'),
          value: this.judgeStats.physical_competitions_count,
        },
        {
          key: 'judge-technical-events',
          label: this.$t('profile.stats.judge_technical_operator_events_label'),
          value: this.judgeStats.technical_operator_competitions_count,
        },
      ];
    },
    judgeStageHighlights() {
      const counters = new Map();

      this.judgeStats.stage_counts.forEach(stage => {
        const key = this.normalizeAthleteStageCode(stage?.stage_code);
        const count = Number(stage?.rounds_count || 0);

        if (!key || count <= 0) {
          return;
        }

        counters.set(key, (counters.get(key) || 0) + count);
      });

      return this.athleteStageOrder()
        .map(key => ({
          key,
          label: this.athleteStageLabel(key),
          value: counters.get(key) || 0,
        }))
        .filter(item => item.value > 0);
    },
    judgeFastestRound() {
      return this.judgeStats.fastest_round;
    },
    judgeBestRound() {
      return this.judgeStats.best_round;
    },
    activeStatsRole() {
      if (this.activeStatsTab && this.availableStatsTabs.includes(this.activeStatsTab)) {
        return this.activeStatsTab;
      }

      if (this.availableStatsTabs.length === 1) {
        return this.availableStatsTabs[0];
      }

      if (this.availableStatsTabs.includes('athlete')) {
        return 'athlete';
      }

      return this.availableStatsTabs[0] || null;
    },
    showStatsTabs() {
      return this.availableStatsTabs.length > 1;
    },
    statsSectionTitle() {
      if (this.activeStatsRole === 'judge') {
        return this.$t('profile.stats.judge_title');
      }
      if (this.activeStatsRole === 'organizer') {
        return this.$t('profile.stats.organizer_title');
      }

      return this.$t('profile.stats.title');
    },
    statsSectionSubtitle() {
      if (this.activeStatsRole === 'judge') {
        return this.$t('profile.stats.judge_subtitle');
      }
      if (this.activeStatsRole === 'organizer') {
        return this.$t('profile.stats.organizer_subtitle');
      }

      return this.$t('profile.stats.subtitle');
    },
    statsCountPill() {
      if (this.activeStatsRole === 'judge') {
        return this.judgeStats.competitions_count;
      }
      if (this.activeStatsRole === 'organizer') {
        return this.organizerStats.competitions_count;
      }

      return this.profileStats.athlete.competitions_count;
    },
    athleteHighlights() {
      const athleteSummary = this.profileStats.athlete.summary;

      return [
        {
          key: 'unique-tricks',
          label: this.tOr('profile.stats.unique_tricks', 'Unique tricks'),
          value: athleteSummary.unique_tricks_count,
        },
        {
          key: 'validated-tricks',
          label: this.tOr('profile.stats.validated_tricks', 'Validated tricks'),
          value: athleteSummary.validated_tricks_count,
        },
        {
          key: 'average-round-normalized',
          label: this.tOr('profile.stats.average_round_score_normalized', 'Average round score / min'),
          value: this.formatStatValue(athleteSummary.average_round_score_normalized),
        },
        {
          key: 'average-round-raw',
          label: this.tOr('profile.stats.average_round_score_raw', 'Average raw round score'),
          value: this.formatStatValue(athleteSummary.average_round_score_raw),
        },
        {
          key: 'best-round',
          label: this.tOr('profile.stats.best_round_score', 'Best round score'),
          value: this.formatStatValue(athleteSummary.best_round_score),
        },
      ];
    },
    athleteStageHighlights() {
      const stageBuckets = new Map();
      const primaryStages = Array.isArray(this.profileStats.athlete.rounds?.by_stage)
        ? this.profileStats.athlete.rounds.by_stage
        : [];
      const fallbackStages = primaryStages.length
        ? []
        : (Array.isArray(this.profileStats.athlete.stages?.averages)
          ? this.profileStats.athlete.stages.averages
          : []);
      const countStages = primaryStages.length || fallbackStages.length
        ? []
        : (Array.isArray(this.profileStats.athlete.stages?.counts)
          ? this.profileStats.athlete.stages.counts
          : []);

      [...primaryStages, ...fallbackStages, ...countStages].forEach(stage => {
        const key = this.normalizeAthleteStageCode(stage?.stage_code);
        if (!key) {
          return;
        }

        const current = stageBuckets.get(key) || {
          key,
          label: this.athleteStageLabel(key),
          roundsCount: 0,
          bestScore: null,
        };

        const roundsCount = Number(stage?.rounds_count || 0);
        const bestScore = Number(stage?.best_total_score);

        if (Number.isFinite(roundsCount) && roundsCount > 0) {
          current.roundsCount += roundsCount;
        }

        if (Number.isFinite(bestScore)) {
          current.bestScore = current.bestScore === null ? bestScore : Math.max(current.bestScore, bestScore);
        }

        stageBuckets.set(key, current);
      });

      return this.athleteStageOrder()
        .map(key => {
          const stage = stageBuckets.get(key);
          if (!stage) {
            return null;
          }

          return {
            key,
            label: stage.label,
            roundsCount: stage.roundsCount,
            bestScore: stage.bestScore,
            bestScoreDisplay: this.formatStatValue(stage.bestScore),
            roundsCountLabel: `${this.tOr('profile.stats.rounds_count_short', 'Rounds')}: ${stage.roundsCount}`,
          };
        })
        .filter(item => item && (item.roundsCount > 0 || item.bestScore !== null));
    },
    topTricks() {
      return Array.isArray(this.profileStats.athlete.tricks?.top_tricks)
        ? this.profileStats.athlete.tricks.top_tricks
        : [];
    },
    trickLevels() {
      return Array.isArray(this.profileStats.athlete.tricks?.by_level)
        ? this.profileStats.athlete.tricks.by_level
        : [];
    },
    trickCategories() {
      return Array.isArray(this.profileStats.athlete.tricks?.by_category)
        ? this.profileStats.athlete.tricks.by_category
        : [];
    },
    comboStats() {
      return this.profileStats.athlete.combos || { total: 0, longest_combo: null, highest_scoring_combo: null, top_combos: [] };
    },
    comboSpotlights() {
      const spotlights = [];
      const longest = this.comboStats.longest_combo;
      const highest = this.comboStats.highest_scoring_combo;

      if (longest) {
        spotlights.push({
          key: this.comboIdentity(longest),
          combo: longest,
          badges: [this.tOr('profile.stats.combo_longest', 'Longest combo')],
        });
      }

      if (highest) {
        const existing = spotlights.find(item => item.key === this.comboIdentity(highest));
        if (existing) {
          existing.badges.push(this.tOr('profile.stats.combo_highest_score', 'Highest score'));
        } else {
          spotlights.push({
            key: this.comboIdentity(highest),
            combo: highest,
            badges: [this.tOr('profile.stats.combo_highest_score', 'Highest score')],
          });
        }
      }

      return spotlights;
    },
    penaltyStats() {
      return this.profileStats.athlete.penalties || { available: false, total: 0, summary: {}, by_type: [] };
    },
    penaltyTypes() {
      return Array.isArray(this.penaltyStats.by_type) ? this.penaltyStats.by_type : [];
    },
    penaltySummaryItems() {
      const summary = this.penaltyStats.summary || {};

      return ['20%', '50%', '80%'].map(label => ({
        label,
        count: Number(summary[label] || 0),
      }));
    },
    showAthleteAnalytics() {
      const charts = this.profileStats.athlete.charts || {};
      return Boolean((charts.tricks_by_category || []).length || (charts.tricks_by_level || []).length);
    },
    hasJudgeStats() {
      const stats = this.judgeStats;
      return stats.competitions_count > 0 || stats.rounds_scored_count > 0;
    },
    organizerStats() {
      return this.profileStats.organizer;
    },
    hasOrganizerStats() {
      return this.organizerStats.competitions_count > 0;
    },
    sponsorLabel() {
      return this.user?.brand_sponsor?.name
          || this.user?.brand_sponsor_name
          || this.user?.brand_sponsor
          || this.$t('profile.sponsor_none');
    },
    groupLabel() {
      return this.user?.group_name || this.$t('profile.group_none');
    },
    isaMember() {
      return !!this.user?.isa_member;
    },
    isaMemberText() {
      return this.isaMember
          ? this.$t('profile.isa_member_yes')
          : this.$t('profile.isa_member_no');
    }
  },
  mounted() {
    if (this.user) {
      this.listenToUserUpdates(this.user.id);
    }
    this.scheduleChartsRender();
  },
  beforeUnmount() {
    this.destroyCharts();
  },
  watch: {
    profileStats: {
      deep: true,
      handler() {
        if (this.activeStatsRole === 'athlete' && !this.statsLoading) {
          this.scheduleChartsRender();
          return;
        }

        this.destroyCharts();
      }
    },
    activeStatsRole() {
      if (this.activeStatsRole === 'athlete' && !this.statsLoading) {
        this.scheduleChartsRender();
        return;
      }

      this.destroyCharts();
    },
    statsLoading(isLoading) {
      if (isLoading) {
        this.destroyCharts();
        return;
      }

      if (this.activeStatsRole === 'athlete') {
        this.scheduleChartsRender();
      }
    }
  },
  methods: {
    ...mapActions('user', ['setLogout','setUser']),
    ...mapActions('round', ['setRoundCompetitionIdToView','setAllRounds','setAllPodiums']),
    ...mapActions('competition', ['setCompetitionIdToView','setCompetition']),
    ...mapActions('competition_users', ['setCompetitionUsers']),
    listenToUserUpdates(userId) {
      echoService.channel(`users.${userId}`)
          .listen('.UserUpdated', (e) => {
            const updatedUser = {
              ...this.user,
              ...e.user // solo actualiza los campos nuevos
            };

            this.setUser(updatedUser);
          })
          .error(error => {
            console.error('❌ Error en WebSocket:', error);
          });
    },
    formatInstagram(link) {
      if (link.startsWith('@')) {
        return `https://instagram.com/${link.slice(1)}`;
      }else if(link.startsWith('https')){
        return `${link}`;
      }else{
        return `https://instagram.com/${link}`;
      }
    },
    formatYoutube(link) {
      if (link.startsWith('@')) {
        return `https://youtube.com/${link}`;
      }else if(link.startsWith('https')){
        return `${link}`;
      }else{
        return `https://youtube.com/@${link}`;
      }
    },
    formatWeb(link) {
      if (link.startsWith('https') || link.startsWith('http')) {
        return `${link}`;
      }else{
        return `https://${link}`;
      }
    },
    tOr(key, fallback) {
      return this.$te && this.$te(key) ? this.$t(key) : fallback;
    },
    athleteStageOrder() {
      return ['qualification', 'stage5', 'stage4', 'stage3', 'stage2', 'stage1'];
    },
    normalizeAthleteStageCode(rawCode) {
      if (!rawCode) {
        return null;
      }

      const code = String(rawCode).trim().toLowerCase();

      if (code === 'qualification') {
        return 'qualification';
      }

      if (/^qualification_\d+$/.test(code)) {
        return 'qualification';
      }

      const aliases = {
        stage5: 'stage5',
        round_32: 'stage5',
        sixteenthfinal: 'stage5',
        sixteenth_final: 'stage5',
        stage4: 'stage4',
        round_16: 'stage4',
        eighthfinal: 'stage4',
        eighth_final: 'stage4',
        stage3: 'stage3',
        round_8: 'stage3',
        quarterfinal: 'stage3',
        quarter_final: 'stage3',
        stage2: 'stage2',
        round_4: 'stage2',
        semifinal: 'stage2',
        semi_final: 'stage2',
        stage1: 'stage1',
        final: 'stage1',
      };

      return aliases[code] || null;
    },
    athleteStageLabel(key) {
      if (key === 'qualification') {
        return this.tOr('profile.stats.qualifications_stage', 'Qualifications');
      }

      if (/^stage\d+$/.test(key)) {
        return this.$t(`rounds.${key}`);
      }

      return key;
    },
    tabLabel(tab) {
      if (tab === 'athlete') {
        return this.tOr('profile.stats.tab_athlete', 'Athlete');
      }
      if (tab === 'judge') {
        return this.tOr('profile.stats.tab_judge', 'Judge');
      }
      if (tab === 'organizer') {
        return this.tOr('profile.stats.tab_organizer', 'Organizer');
      }

      return tab;
    },
    formatStatValue(value) {
      if (value === null || value === undefined || value === '') {
        return '—';
      }

      const numericValue = Number(value);
      if (!Number.isFinite(numericValue)) {
        return String(value);
      }

      if (Number.isInteger(numericValue)) {
        return String(numericValue);
      }

      return numericValue.toFixed(2);
    },
    formatDurationSeconds(value) {
      const numericValue = Number(value);

      if (!Number.isFinite(numericValue) || numericValue < 0) {
        return '—';
      }

      const totalSeconds = Math.round(numericValue);
      const minutes = Math.floor(totalSeconds / 60);
      const seconds = totalSeconds % 60;

      if (minutes <= 0) {
        return `${seconds}s`;
      }

      return `${minutes}m ${String(seconds).padStart(2, '0')}s`;
    },
    scheduleChartsRender() {
      this.chartRenderNonce += 1;
      const currentNonce = this.chartRenderNonce;

      this.$nextTick(() => {
        if (currentNonce !== this.chartRenderNonce) {
          return;
        }

        this.renderAthleteCharts();
      });
    },
    isMobileChartViewport() {
      if (typeof window === 'undefined') {
        return false;
      }

      return window.matchMedia('(max-width: 576px)').matches;
    },
    destroyCharts() {
      this.chartRenderNonce += 1;

      if (this.trickCategoryChartInstance) {
        this.trickCategoryChartInstance.destroy();
        this.trickCategoryChartInstance = null;
      }
      if (this.trickLevelChartInstance) {
        this.trickLevelChartInstance.destroy();
        this.trickLevelChartInstance = null;
      }
    },
    getChartContext(canvasRef) {
      if (!canvasRef || typeof canvasRef.getContext !== 'function') {
        return null;
      }

      if ('isConnected' in canvasRef && !canvasRef.isConnected) {
        return null;
      }

      return canvasRef.getContext('2d');
    },
    renderAthleteCharts() {
      if (this.activeStatsRole !== 'athlete' || this.statsLoading || !this.showAthleteAnalytics) {
        this.destroyCharts();
        return;
      }

      this.destroyCharts();

      const charts = this.profileStats.athlete.charts || {};
      const categoryCanvas = this.$refs.trickCategoryChart;
      const trickLevelCanvas = this.$refs.trickLevelChart;
      const categoryContext = this.getChartContext(categoryCanvas);
      const trickLevelContext = this.getChartContext(trickLevelCanvas);
      const isMobile = this.isMobileChartViewport();

      const sharedTickColor = '#6b7280';
      const legendLabelConfig = {
        boxWidth: isMobile ? 10 : 12,
        boxHeight: isMobile ? 10 : 12,
        padding: isMobile ? 10 : 14,
        font: {
          size: isMobile ? 10 : 12,
        },
        color: '#374151',
      };

      if (categoryContext && Array.isArray(charts.tricks_by_category) && charts.tricks_by_category.length) {
        this.trickCategoryChartInstance = new Chart(categoryContext, {
          type: 'doughnut',
          data: {
            labels: charts.tricks_by_category.map(item => item.label),
            datasets: [{
              data: charts.tricks_by_category.map(item => Number(item.value) || 0),
              backgroundColor: ['#f59e0b', '#4c56af', '#10b981', '#ef4444', '#0ea5e9', '#a855f7', '#f97316', '#64748b'],
              borderWidth: 0,
            }],
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            cutout: isMobile ? '54%' : '62%',
            layout: {
              padding: isMobile ? 4 : 8,
            },
            plugins: {
              legend: {
                position: 'bottom',
                align: 'start',
                labels: legendLabelConfig,
              },
            },
          },
        });
      }

      if (trickLevelContext && Array.isArray(charts.tricks_by_level) && charts.tricks_by_level.length) {
        this.trickLevelChartInstance = new Chart(trickLevelContext, {
          type: 'bar',
          data: {
            labels: charts.tricks_by_level.map(item => item.label),
            datasets: [{
              label: this.tOr('profile.stats.validated_tricks', 'Validated tricks'),
              data: charts.tricks_by_level.map(item => Number(item.validated_count) || Number(item.value) || 0),
              backgroundColor: '#4c56af',
              borderRadius: 8,
              maxBarThickness: isMobile ? 22 : 34,
            }],
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            layout: {
              padding: {
                top: 4,
                right: isMobile ? 0 : 8,
                bottom: 0,
                left: 0,
              },
            },
            plugins: {
              legend: {
                display: false,
              },
            },
            scales: {
              x: {
                ticks: {
                  autoSkip: false,
                  maxRotation: 0,
                  minRotation: 0,
                  color: sharedTickColor,
                  font: {
                    size: isMobile ? 10 : 12,
                  },
                },
                grid: {
                  display: false,
                },
              },
              y: {
                beginAtZero: true,
                ticks: {
                  precision: 0,
                  color: sharedTickColor,
                  font: {
                    size: isMobile ? 10 : 12,
                  },
                },
                grid: {
                  color: 'rgba(148, 163, 184, 0.2)',
                },
              },
            },
          },
        });
      }
    },
    levelKey(level = {}) {
      return level.level_id || level.label || 'unknown';
    },
    comboIdentity(combo = {}) {
      return `${combo.round_id || 'round'}-${combo.combo_number || 'combo'}`;
    },
    comboKey(spotlight = {}) {
      return spotlight.key || this.comboIdentity(spotlight.combo || {});
    },
    isLevelExpanded(level) {
      return this.expandedLevels.includes(this.levelKey(level));
    },
    toggleLevel(level) {
      const key = this.levelKey(level);
      if (this.expandedLevels.includes(key)) {
        this.expandedLevels = this.expandedLevels.filter(levelKey => levelKey !== key);
        return;
      }
      this.expandedLevels = [...this.expandedLevels, key];
    },
    isComboExpanded(spotlight) {
      return this.expandedCombos.includes(this.comboKey(spotlight));
    },
    toggleCombo(spotlight) {
      const key = this.comboKey(spotlight);
      if (this.expandedCombos.includes(key)) {
        this.expandedCombos = this.expandedCombos.filter(item => item !== key);
        return;
      }
      this.expandedCombos = [...this.expandedCombos, key];
    },
    penaltyKey(penalty = {}) {
      return penalty.dub_id || penalty.label || 'penalty';
    },
    isPenaltyExpanded(penalty) {
      return this.expandedPenalties.includes(this.penaltyKey(penalty));
    },
    togglePenalty(penalty) {
      const key = this.penaltyKey(penalty);
      if (this.expandedPenalties.includes(key)) {
        this.expandedPenalties = this.expandedPenalties.filter(item => item !== key);
        return;
      }
      this.expandedPenalties = [...this.expandedPenalties, key];
    },
    navigateToCompetition(competitionId) {
      if (!competitionId) {
        return;
      }
      this.$router.push({
        path: '/competitionView',
        query: { id: competitionId },
      });
    },
    async navigateToScore(placement) {
      if (!placement) {
        return;
      }
      const competitionId = placement.competition_id;
      const roundId = placement.round_id;

      if (competitionId) {
        await this.setCompetitionIdToView(competitionId);
        await this.ensureCompetitionContext(competitionId, roundId);
      }

      if (roundId) {
        await this.setRoundCompetitionIdToView(roundId);
        await this.$router.push({ path: '/score' });
        return;
      }

      this.navigateToCompetition(competitionId);
    },
    async ensureCompetitionContext(competitionId, roundId) {
      if (!competitionId) {
        return;
      }

      try {
        const token = this.$store.getters['user/getToken'] || this.$store.getters.getToken;
        if (!token) {
          return;
        }

        const response = await this.$axios.get(`/competition/${competitionId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        const competition = response.data?.competition;
        if (!competition) {
          return;
        }

        await Promise.all([
          this.setCompetition(competition),
          this.setCompetitionUsers(competition.competition_users || []),
          this.setAllRounds(competition.rounds || []),
          this.setAllPodiums(competition.podiums || []),
        ]);
      } catch (error) {
        if (roundId && [401, 403].includes(error?.response?.status)) {
          await this.hydrateCompetitionContextFromRound(roundId, competitionId);
        }
        console.error('❌ Error al cargar la competencia desde el perfil:', error);
      }
    },
    async hydrateCompetitionContextFromRound(roundId, competitionId) {
      if (!roundId) {
        return;
      }

      try {
        const token = this.$store.getters['user/getToken'] || this.$store.getters.getToken;
        if (!token) {
          return;
        }

        const response = await this.$axios.get(`/get_round/${roundId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const round = response.data?.round;
        if (!round) {
          return;
        }

        const competition = round.competition || {
          id: competitionId || round.competition_id,
          name: round.competition_name,
        };

        await Promise.all([
          this.setCompetition(competition),
          this.setCompetitionUsers(competition.competition_users || []),
          this.setAllRounds((competition.rounds && competition.rounds.length) ? competition.rounds : [round]),
          this.setAllPodiums(competition.podiums || []),
        ]);
      } catch (fallbackError) {
        console.error('❌ Error al hidratar la competencia desde la ronda:', fallbackError);
      }
    },
    getCompetitionCountryCode(entry = {}) {
      const code = entry.country_code || entry.country?.code || entry.country?.iso2;
      return code ? String(code).trim().toUpperCase() : '';
    },
    getCompetitionCountryName(entry = {}) {
      const name = entry.country_name || entry.country?.name;
      return name ? String(name).trim() : '';
    },
    formatEventDate(entry = {}) {
      const start = entry.date_start || entry.start_date || entry.date || entry.event_date;
      const end = entry.date_end || entry.end_date || entry.event_end;
      const startLabel = this.formatDateLabel(start);
      const endLabel = this.formatDateLabel(end);
      if (startLabel && endLabel && startLabel !== endLabel) {
        return `${startLabel} - ${endLabel}`;
      }
      if (startLabel) {
        return startLabel;
      }
      if (endLabel) {
        return endLabel;
      }
      return this.$t('profile.stats.date_unknown');
    },
    formatDateLabel(value) {
      if (!value) {
        return '';
      }
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) {
        return '';
      }
      return date.toLocaleDateString();
    },
    codeToFlag(code = '') {
      if (!code || code.length < 2) {
        return '';
      }
      const letters = code
          .toUpperCase()
          .replace(/[^A-Z]/g, '')
          .slice(0, 2);
      if (letters.length < 2) {
        return '';
      }
      return letters
          .split('')
          .map(char => String.fromCodePoint(127397 + char.charCodeAt(0)))
          .join('');
    },
    medalClass(position) {
      const pos = Number(position);
      if (pos === 1) return 'gold';
      if (pos === 2) return 'silver';
      if (pos === 3) return 'bronze';
      return 'neutral';
    }

  }
};
</script>

<style scoped>
.profile-img {
  width: 100px;
  height: 100px;
  object-fit: cover;
  border-radius: 50%;
  border: 4px solid white;
  position: absolute;
  top: -50px;
  left: 50%;
  transform: translateX(-50%);
  background-color: white;
}

.card-container {
  margin-top: 35px;
}

.profile-back-button {
  position: absolute;
  top: 10px;
  left: 18px;
  z-index: 3;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  border: 0;
  border-radius: 999px;
  padding: 8px 14px;
  background: rgba(255, 255, 255, 0.92);
  color: #1f2937;
  font-weight: 600;
  box-shadow: 0 8px 18px rgba(15, 23, 42, 0.18);
}

.profile-back-button:hover {
  background: #ffffff;
}

.profile-back-button:focus-visible {
  outline: 2px solid #1f2937;
  outline-offset: 2px;
}

.section-box {
  background-color: #f8f9fa;
  padding: 1rem;
  border-radius: 10px;
  margin-bottom: 1.5rem;
}

.section-title {
  font-weight: 600;
  margin-bottom: 0.5rem;
}

.roles-list span {
  display: inline-block;
  margin-right: 0.5rem;
  margin-bottom: 0.5rem;
}

.social-icons a {
  color: #333;
  transition: color 0.2s;
}

.social-icons a:hover {
  color: #007bff;
}

.info-line .pill {
  background: #f5f6f8;
  border: 1px solid #e5e7eb;
  border-radius: 999px;
  padding: 0.4rem 0.85rem;
  font-size: 0.95rem;
}

.info-line i {
  color: #111827;
}

.edit-btn {
  position: absolute;
  top: 15px;
  right: 15px;
}

.stats-section {
  margin-top: 1rem;
  padding: 1.5rem 1rem;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  background: #fff;
}

.stats-tabs {
  display: flex;
  gap: 0.6rem;
  flex-wrap: wrap;
  margin-bottom: 1rem;
}

.stats-tab {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #d1d5db;
  background: #f9fafb;
  color: #374151;
  border-radius: 999px;
  padding: 0.45rem 0.9rem;
  font-weight: 600;
}

.stats-tab.active {
  border-color: #111827;
  background: #111827;
  color: #ffffff;
}

.stats-loading-state {
  padding: 1rem;
  border: 1px dashed #d1d5db;
  border-radius: 10px;
  color: #6b7280;
  text-align: left;
  background: #f9fafb;
}

.stats-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  gap: 0.75rem;
  min-width: 0;
}

.section-label {
  font-size: 0.85rem;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #6b7280;
  margin-bottom: 0.2rem;
}

.stats-heading {
  margin: 0;
}

.count-pill {
  background: #111827;
  color: #fff;
  padding: 0.5rem 0.85rem;
  border-radius: 999px;
  font-weight: 600;
  max-width: 100%;
}

.stats-summary {
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
}

.stats-highlights {
  margin-top: 1rem;
  display: grid;
  gap: 0.75rem;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
}

.stats-highlights-compact {
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
}

.stage-rounds-section {
  margin-top: 1rem;
}

.stage-rounds-header {
  margin-bottom: 0.5rem;
}

.stage-highlight-card {
  min-height: 100%;
}

.stage-highlight-caption,
.stage-highlight-meta {
  font-size: 0.78rem;
  color: #6b7280;
}

.highlight-card {
  padding: 0.9rem 1rem;
  border-radius: 10px;
  border: 1px solid #e5e7eb;
  background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  min-width: 0;
  max-width: 100%;
}

.highlight-value {
  font-size: 1.35rem;
  font-weight: 700;
  color: #111827;
}

.summary-card {
  flex: 1;
  min-width: 160px;
  padding: 1rem;
  border-radius: 10px;
  background: #f9fafb;
  border: 1px solid #ececec;
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
  max-width: 100%;
}

.summary-label {
  font-size: 0.85rem;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.06em;
}

.summary-value {
  font-size: 1.75rem;
  font-weight: 600;
}

.podium-badges {
  display: flex;
  justify-content: space-between;
  gap: 0.5rem;
}

.podium-badge {
  flex: 1;
  border-radius: 8px;
  padding: 0.5rem;
  text-align: center;
  background: #fff;
  border: 1px solid #e5e7eb;
}

.podium-badge span {
  display: block;
  font-size: 0.75rem;
  color: #4b5563;
  text-transform: uppercase;
}

.podium-badge strong {
  display: block;
  font-size: 1.1rem;
  margin-top: 0.2rem;
}

.podium-badge.gold {
  border-color: #fcd34d;
}

.podium-badge.silver {
  border-color: #cbd5f5;
}

.podium-badge.bronze {
  border-color: #f4b183;
}

.stats-columns {
  margin-top: 1.25rem;
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
  min-width: 0;
}

.stats-columns-secondary {
  margin-top: 1rem;
}

.stats-analytics {
  margin-top: 1rem;
  display: grid;
  gap: 1rem;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  min-width: 0;
}

.analytics-card {
  border: 1px solid #ececec;
  border-radius: 10px;
  padding: 1rem;
  background: #fdfdfd;
  overflow: hidden;
  min-width: 0;
}

.chart-box {
  position: relative;
  min-height: 260px;
}

.chart-box canvas {
  width: 100% !important;
  height: 100% !important;
}

.stats-column {
  flex: 1;
  min-width: 260px;
  border: 1px solid #ececec;
  border-radius: 10px;
  padding: 1rem;
  background: #fdfdfd;
  max-width: 100%;
  overflow: hidden;
  box-sizing: border-box;
}

.column-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.75rem;
  gap: 0.75rem;
  min-width: 0;
}

.penalty-column-header {
  align-items: center;
  gap: 0.5rem;
  flex-wrap: nowrap;
}

.penalty-header-main {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 0.4rem;
  min-width: 0;
  flex: 1 1 auto;
}

.penalty-header-main h5 {
  margin: 0;
  white-space: nowrap;
}

.column-count {
  background: #e5e7eb;
  border-radius: 999px;
  padding: 0.2rem 0.6rem;
  font-size: 0.85rem;
  max-width: 100%;
  flex-shrink: 0;
}

.competition-list,
.placement-list,
.detail-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  max-height: 340px;
  overflow-y: auto;
  padding-right: 0.2rem;
  scrollbar-gutter: stable;
}

.competition-item,
.placement-item,
.detail-item {
  display: flex;
  gap: 0.75rem;
  padding: 0.75rem;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
  background: #fff;
  cursor: pointer;
  transition: box-shadow 0.2s ease, transform 0.2s ease;
  min-width: 0;
  max-width: 100%;
  box-sizing: border-box;
}

.competition-item:focus,
.placement-item:focus,
.detail-item:focus,
.competition-item:hover,
.placement-item:hover,
.detail-item:hover {
  outline: none;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  transform: translateY(-1px);
}

.detail-item {
  justify-content: space-between;
  align-items: center;
  cursor: default;
}

.detail-expandable {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.detail-toggle {
  width: 100%;
  border: 1px solid #e5e7eb;
  background: #f8fafc;
  text-align: left;
}

.detail-toggle.expanded {
  border-color: #c7d2fe;
  background: #eef2ff;
}

.detail-main {
  min-width: 0;
  flex: 1;
}

.detail-meta {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.35rem;
  font-size: 0.85rem;
  color: #4b5563;
  min-width: 0;
}

.detail-side {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.25rem;
  min-width: 0;
  max-width: 100%;
}

.detail-side-inline {
  flex-direction: row;
  align-items: center;
}

.detail-side-note {
  color: #6b7280;
}

.meta-pill {
  display: inline-flex;
  align-items: center;
  padding: 0.15rem 0.45rem;
  border-radius: 999px;
  background: #eef2ff;
  color: #3730a3;
  font-size: 0.76rem;
  font-weight: 600;
  min-width: 0;
  max-width: 100%;
  overflow-wrap: anywhere;
}

.meta-pill-warning {
  background: #fff7ed;
  color: #c2410c;
}

.level-pill {
  min-width: 2.25rem;
  padding: 0.2rem 0.65rem;
  border-radius: 999px;
  background: #111827;
  color: #fff;
  font-weight: 600;
  text-align: center;
  max-width: 100%;
}

.detail-chevron {
  font-size: 1.25rem;
  line-height: 1;
  color: #374151;
}

.level-tricks-panel {
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  background: #ffffff;
  padding: 0.75rem;
  min-width: 0;
  overflow: hidden;
}

.level-tricks-list {
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
  max-height: 240px;
  overflow-y: auto;
}

.level-trick-item {
  display: flex;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.65rem 0.75rem;
  border-radius: 10px;
  background: #f8fafc;
  min-width: 0;
  max-width: 100%;
  box-sizing: border-box;
}

.combo-panels {
  display: flex;
  flex-direction: column;
  gap: 0.9rem;
}

.combo-card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.75rem;
}

.combo-card-meta {
  margin-top: 0.6rem;
}

.combo-badges {
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
}

.mini-pill {
  display: inline-flex;
  align-items: center;
  padding: 0.2rem 0.55rem;
  border-radius: 999px;
  background: #111827;
  color: #ffffff;
  font-size: 0.76rem;
  font-weight: 600;
}

.combo-components-list {
  margin-top: 0.75rem;
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
}

.combo-component-item {
  display: flex;
  align-items: flex-start;
  gap: 0.65rem;
  padding: 0.65rem 0.75rem;
  border-radius: 10px;
  background: #f8fafc;
  min-width: 0;
  max-width: 100%;
  box-sizing: border-box;
}

.combo-index {
  width: 1.8rem;
  height: 1.8rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 999px;
  background: #111827;
  color: #ffffff;
  font-size: 0.8rem;
  font-weight: 700;
  flex-shrink: 0;
}

.penalty-summary-pills {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 0.75rem;
}

.penalty-summary-pills-inline {
  margin-bottom: 0;
  gap: 0.25rem;
  flex-wrap: nowrap;
  min-width: 0;
}

.penalty-summary-pills-inline .penalty-summary-pill {
  padding: 0.2rem 0.45rem;
  font-size: 0.74rem;
  white-space: nowrap;
}

.penalty-summary-pill {
  display: inline-flex;
  align-items: center;
  padding: 0.25rem 0.65rem;
  border-radius: 999px;
  background: #fff7ed;
  color: #c2410c;
  font-size: 0.8rem;
  font-weight: 600;
  min-width: 0;
  max-width: 100%;
}

.penalty-list {
  max-height: none;
}

.competition-avatar {
  width: 48px;
  height: 48px;
  object-fit: cover;
  border-radius: 8px;
}

.competition-info,
.placement-info {
  min-width: 0;
  max-width: 100%;
}

.competition-name {
  font-weight: 600;
  margin: 0;
  text-align: left;
  min-width: 0;
  max-width: 100%;
  overflow-wrap: anywhere;
}

.competition-meta {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.35rem;
  margin-top: 0.25rem;
}

.competition-country {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  font-size: 0.85rem;
  color: #4b5563;
  text-transform: uppercase;
}

.country-flag {
  font-size: 1rem;
  line-height: 1;
  /* Emoji-capable stack so flags render across platforms (incl. Windows) */
  font-family: "Twemoji Mozilla", "Noto Color Emoji", "Segoe UI Emoji", "Apple Color Emoji", "Segoe UI", system-ui, sans-serif;
}

.country-code {
  letter-spacing: 0.08em;
}

.competition-date {
  font-size: 0.85rem;
  color: #4b5563;
}

.meta-divider {
  color: #d1d5db;
}

.meta-fallback {
  font-size: 0.85rem;
  color: #9ca3af;
  font-style: italic;
}

.placement-medal {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  color: #111827;
}

.placement-medal.gold {
  background: #fef3c7;
  border: 2px solid #f59e0b;
}

.placement-medal.silver {
  background: #f3f4f6;
  border: 2px solid #94a3b8;
}

.placement-medal.bronze {
  background: #fef7ec;
  border: 2px solid #d97706;
}

.placement-medal.neutral {
  background: #e5e7eb;
  border: 2px solid #9ca3af;
}

.placement-info {
  flex: 1;
}

.placement-meta {
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
  font-size: 0.85rem;
  color: #6b7280;
  align-items: center;
}

.placement-score-wrapper {
  display: flex;
  justify-content: center;
  margin-top: 0.25rem;
}

.score-badge {
  background: #eef2ff;
  padding: 0.15rem 0.5rem;
  border-radius: 999px;
  min-width: 0;
  max-width: 100%;
  overflow-wrap: anywhere;
}

.score-badge.centered {
  min-width: 120px;
  text-align: center;
}

.stats-empty {
  margin: 0;
  color: #9ca3af;
  font-style: italic;
}

.judge-stats {
  margin-top: 1.5rem;
  padding-top: 1.25rem;
  border-top: 1px solid #e5e7eb;
}

.judge-header {
  margin-bottom: 1rem;
  text-align: left;
}

.judge-summary {
  gap: 1rem;
}

.judge-card {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
}

.judge-detail-columns {
  margin-top: 1rem;
  align-items: stretch;
}

.judge-detail-column {
  flex: 1 1 0;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  gap: 0.35rem;
  min-width: 0;
  overflow: hidden;
}

.judge-detail-card {
  flex: 0 0 auto;
  display: flex;
  justify-content: space-between;
  gap: 0.75rem;
  align-items: flex-start;
  width: 100%;
  min-width: 0;
  padding: 0.85rem 0.9rem;
  border: 1px solid #e5e7eb;
  border-radius: 14px;
  background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
  box-shadow: 0 8px 18px rgba(15, 23, 42, 0.05);
  overflow: hidden;
}

.judge-detail-card .detail-main {
  min-width: 0;
  flex: 1 1 auto;
}

.judge-detail-card .competition-name {
  min-width: 0;
  overflow-wrap: anywhere;
}

.judge-detail-card .detail-meta {
  min-width: 0;
  overflow-wrap: anywhere;
}

.judge-detail-card .meta-pill {
  min-width: 0;
  max-width: 100%;
  overflow-wrap: anywhere;
}

.judge-detail-card .detail-side {
  flex-shrink: 0;
  max-width: 100%;
  align-self: flex-start;
}

.judge-detail-card .score-badge {
  white-space: nowrap;
}

.judge-detail-card .score-badge.centered {
  min-width: 0;
  width: auto;
  max-width: 100%;
}

.judge-detail-columns > .stats-column {
  min-width: 0;
  overflow: hidden;
}

.judge-detail-card .score-badge.centered {
  min-width: 0;
  max-width: 100%;
}

.organizer-stats {
  margin-top: 1.5rem;
  padding-top: 1.25rem;
  border-top: 1px solid #e5e7eb;
}

.organizer-header {
  margin-bottom: 1rem;
  text-align: left;
}

.organizer-summary {
  gap: 1rem;
}

.judge-tab-summary,
.organizer-tab-summary {
  margin-top: 0.5rem;
}

.organizer-card {
  background: #fff7ed;
  border: 1px solid #fed7aa;
}

@media (max-width: 768px) {
  .card-container,
  .card-container > .card,
  .card-container > .card > .card,
  .card-body,
  .stats-section,
  .section-box {
    min-width: 0;
    max-width: 100%;
    box-sizing: border-box;
  }

  .card-container {
    padding-left: 0.5rem;
    padding-right: 0.5rem;
  }

  .card-container > .card,
  .card-container > .card > .card {
    padding-left: 0.75rem !important;
    padding-right: 0.75rem !important;
  }

  .profile-back-button {
    top: 8px;
    left: 10px;
    padding: 7px 12px;
  }

  .stats-header {
    flex-direction: column;
    align-items: flex-start;
  }

  .count-pill {
    align-self: flex-start;
    padding: 0.4rem 0.7rem;
    font-size: 0.82rem;
  }

  .podium-badges {
    flex-direction: column;
  }

  .stats-summary {
    flex-direction: column;
  }

  .podium-badge {
    padding: 0.4rem 0.5rem;
  }

  .podium-badge span {
    font-size: 0.68rem;
  }

  .podium-badge strong {
    font-size: 0.98rem;
  }

  .stats-highlights {
    grid-template-columns: 1fr;
  }

  .summary-label {
    font-size: 0.74rem;
  }

  .summary-value {
    font-size: 1.45rem;
  }

  .highlight-value {
    font-size: 1.15rem;
  }

  .summary-card,
  .highlight-card,
  .stats-column,
  .analytics-card {
    min-width: 0;
    width: 100%;
  }

  .stats-tabs {
    gap: 0.45rem;
  }

  .stats-tab {
    width: 100%;
  }

  .stats-columns {
    flex-direction: column;
  }

  .stats-column,
  .judge-detail-column {
    min-width: 0;
    width: 100%;
    flex-basis: 100%;
  }

  .column-header {
    align-items: flex-start;
    flex-wrap: wrap;
  }

  .penalty-column-header {
    align-items: flex-start;
    gap: 0.35rem;
    flex-wrap: wrap;
  }

  .penalty-header-main {
    gap: 0.3rem;
    flex-wrap: wrap;
  }

  .penalty-summary-pills-inline {
    gap: 0.2rem;
    flex-wrap: wrap;
  }

  .penalty-summary-pills-inline .penalty-summary-pill {
    padding: 0.18rem 0.38rem;
    font-size: 0.7rem;
    white-space: normal;
  }

  .stats-analytics {
    grid-template-columns: 1fr;
  }

  .analytics-card {
    padding: 0.8rem;
  }

  .chart-box {
    min-height: 190px;
  }

  .detail-item {
    align-items: flex-start;
    flex-direction: column;
  }

  .competition-list,
  .placement-list {
    gap: 0.55rem;
    max-height: 320px;
  }

  .competition-item,
  .placement-item {
    flex-direction: row;
    align-items: flex-start;
    gap: 0.55rem;
    padding: 0.6rem;
  }

  .placement-item {
    gap: 0.5rem;
  }

  .competition-avatar {
    width: 40px;
    height: 40px;
  }

  .placement-medal {
    width: 38px;
    height: 38px;
    font-size: 0.82rem;
    border-width: 1.5px;
  }

  .detail-side {
    width: 100%;
    align-items: flex-start;
  }

  .detail-side-inline {
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 0.5rem;
  }

  .score-badge.centered,
  .column-count,
  .level-pill {
    min-width: 0;
    width: auto;
    max-width: 100%;
  }

  .score-badge {
    padding: 0.12rem 0.42rem;
    font-size: 0.78rem;
  }

  .score-badge.centered {
    font-size: 0.78rem;
  }

  .column-count,
  .level-pill {
    font-size: 0.76rem;
    padding-top: 0.16rem;
    padding-bottom: 0.16rem;
  }

  .competition-item,
  .placement-item,
  .level-trick-item,
  .combo-component-item,
  .judge-detail-card {
    overflow: hidden;
  }

  .judge-detail-card {
    align-items: flex-start;
    flex-direction: column;
  }

  .competition-info,
  .placement-info {
    flex: 1 1 auto;
    width: 100%;
    min-width: 0;
  }

  .competition-avatar,
  .placement-medal {
    flex-shrink: 0;
  }

  .competition-name {
    font-size: 0.9rem;
    line-height: 1.2;
  }

  .competition-meta,
  .detail-meta {
    gap: 0.3rem;
  }

  .competition-meta,
  .placement-meta,
  .competition-date,
  .competition-country,
  .meta-fallback {
    font-size: 0.76rem;
    line-height: 1.2;
  }

  .placement-score-wrapper {
    justify-content: flex-start;
    margin-top: 0.35rem;
  }

  .placement-item .score-badge.centered {
    min-width: 0;
    width: auto;
    max-width: 100%;
    text-align: left;
  }

  .penalty-header-main h5,
  .competition-name {
    white-space: normal;
  }

  .level-trick-item {
    flex-direction: column;
  }

  .combo-card-header,
  .combo-component-item,
  .judge-detail-card {
    flex-direction: column;
    align-items: flex-start;
  }
}
</style>
